import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Flower2, Sparkles, Package, Tag, Settings } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { eur, Loading, Field, EmptyState } from "@/components/common";
import Section from "@/components/Section";
import { REC_LABELS } from "@/constants/recorrenze";

export default function AdminCatalogo() {
  const [flowers, setFlowers] = useState(null);
  const [plans, setPlans] = useState([]);
  const [offer, setOffer] = useState(null);
  const [settings, setSettings] = useState(null);
  const [flowerForm, setFlowerForm] = useState({ name: "", description: "", price: "", supplement: "", image: "" });
  const [editingFlower, setEditingFlower] = useState(null);
  const [extrasList, setExtrasList] = useState([]);
  const [extraForm, setExtraForm] = useState({ name: "", description: "", price: "", image: "", category: "extra", recurrence: "" });
  const [editingExtra, setEditingExtra] = useState(null);

  const load = () => {
    api.get("/admin/flowers").then((r) => setFlowers(r.data));
    api.get("/admin/plans").then((r) => setPlans(r.data));
    api.get("/admin/offer").then((r) => setOffer(r.data));
    api.get("/admin/settings").then((r) => setSettings(r.data));
    api.get("/admin/extras").then((r) => setExtrasList(r.data));
  };
  useEffect(load, []);

  if (!flowers || !offer || !settings) return <Loading />;

  const saveFlower = async (e) => {
    e.preventDefault();
    const body = {
      ...flowerForm,
      price: Number(flowerForm.price),
      supplement: flowerForm.supplement === "" ? null : Number(flowerForm.supplement),
    };
    try {
      if (editingFlower) await api.patch(`/admin/flowers/${editingFlower}`, body);
      else await api.post("/admin/flowers", body);
      toast.success("Fiore salvato.");
      setFlowerForm({ name: "", description: "", price: "", supplement: "", image: "" });
      setEditingFlower(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      const body = { ...settings, grave_types: settings.grave_types };
      ["trial_price", "single_visit_price", "single_visit_margin", "flower_standard_price", "professional_fee", "platform_margin", "cleaning_ordinary_price", "extra_exit_fee"].forEach((k) => (body[k] = Number(body[k])));
      delete body.key;
      await api.put("/admin/settings", body);
      toast.success("Prezzi aggiornati.");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const saveOffer = async (e) => {
    e.preventDefault();
    try {
      const body = { ...offer };
      ["duration_months", "frequency", "interventions_total"].forEach((k) => (body[k] = Number(body[k])));
      ["price", "price_per_intervention", "compare_price", "savings"].forEach((k) => (body[k] = Number(body[k])));
      delete body.id; delete body.created_at;
      await api.put("/admin/offer", body);
      toast.success("Offerta aggiornata.");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const saveExtra = async (e) => {
    e.preventDefault();
    const body = { ...extraForm, price: Number(extraForm.price), recurrence: extraForm.category === "ricorrenza" ? extraForm.recurrence || null : null };
    try {
      if (editingExtra) await api.patch(`/admin/extras/${editingExtra}`, body);
      else await api.post("/admin/extras", body);
      toast.success("Prodotto salvato.");
      setExtraForm({ name: "", description: "", price: "", image: "", category: "extra", recurrence: "" });
      setEditingExtra(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const num = (obj, setObj, k) => (e) => setObj({ ...obj, [k]: e.target.value });

  return (
    <div className="space-y-4" data-testid="admin-catalogo">
      <Section title="Catalogo fiori" count={flowers.length} icon={Flower2} testid="admin-catalogo-fiori" defaultOpen>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          {flowers.map((f) => (
            <div key={f.id} className={`card-soft overflow-hidden ${!f.active ? "opacity-50" : ""}`} data-testid={`admin-flower-${f.id}`}>
              {f.image && <img src={f.image} alt={f.name} className="w-full aspect-[4/3] object-cover" />}
              <div className="p-4">
                <div className="flex justify-between items-baseline">
                  <p className="font-semibold text-navy text-sm">{f.name}</p>
                  <p className="font-serif text-lg text-navy">{eur(f.price)}</p>
                </div>
                <p className="text-xs text-muted-soft">Supplemento: {f.supplement != null ? eur(f.supplement) : "automatico"}</p>
                <div className="flex gap-2 mt-3">
                  <button data-testid={`edit-flower-${f.id}`} onClick={() => { setEditingFlower(f.id); setFlowerForm({ name: f.name, description: f.description, price: f.price, supplement: f.supplement ?? "", image: f.image }); }} className="text-xs font-semibold text-[#1e3a5f] hover:underline min-h-[44px]">Modifica</button>
                  <button data-testid={`toggle-flower-${f.id}`} onClick={async () => { await api.patch(`/admin/flowers/${f.id}`, { ...f, active: !f.active }); load(); }} className="text-xs font-semibold text-[#4a7c59] hover:underline min-h-[44px]">
                    {f.active ? "Disattiva" : "Attiva"}
                  </button>
                  <button data-testid={`delete-flower-${f.id}`} onClick={async () => { await api.delete(`/admin/flowers/${f.id}`); load(); }} className="text-xs font-semibold text-red-700 hover:underline min-h-[44px]">Elimina</button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={saveFlower} className="card-ivory p-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end" data-testid="flower-form">
          <Field label="Nome"><input data-testid="flower-name" className="input-soft" required value={flowerForm.name} onChange={num(flowerForm, setFlowerForm, "name")} /></Field>
          <Field label="Descrizione"><input data-testid="flower-desc" className="input-soft" value={flowerForm.description} onChange={num(flowerForm, setFlowerForm, "description")} /></Field>
          <Field label="Prezzo €"><input data-testid="flower-price" type="number" step="0.5" min="0" className="input-soft" required value={flowerForm.price} onChange={num(flowerForm, setFlowerForm, "price")} /></Field>
          <Field label="Supplemento € (vuoto = automatico)"><input data-testid="flower-supplement" type="number" step="0.5" min="0" className="input-soft" value={flowerForm.supplement} onChange={num(flowerForm, setFlowerForm, "supplement")} /></Field>
          <Field label="URL fotografia"><input data-testid="flower-image" className="input-soft" value={flowerForm.image} onChange={num(flowerForm, setFlowerForm, "image")} /></Field>
          <button data-testid="flower-save" type="submit" className="btn-primary !py-2.5 text-sm min-h-[44px] lg:col-span-5 sm:col-span-2">
            {editingFlower ? "Salva modifiche" : "Aggiungi fiore"}
          </button>
        </form>
      </Section>

      <Section title="Extra & Ricorrenze" count={extrasList.length} icon={Sparkles} testid="admin-extras">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          {extrasList.map((x) => (
            <div key={x.id} className={`card-soft overflow-hidden ${!x.active ? "opacity-50" : ""}`} data-testid={`admin-extra-${x.id}`}>
              {x.image && <img src={x.image} alt={x.name} className="w-full aspect-[4/3] object-cover" />}
              <div className="p-4">
                <p className="text-[11px] font-bold uppercase tracking-wide text-[#4a7c59]">
                  {x.category === "ricorrenza" ? `Ricorrenza · ${REC_LABELS[x.recurrence] || x.recurrence}` : "Extra"}
                </p>
                <div className="flex justify-between items-baseline gap-2">
                  <p className="font-semibold text-navy text-sm">{x.name}</p>
                  <p className="font-serif text-lg text-navy">{eur(x.price)}</p>
                </div>
                <div className="flex gap-2 mt-3">
                  <button data-testid={`edit-extra-${x.id}`} onClick={() => { setEditingExtra(x.id); setExtraForm({ name: x.name, description: x.description, price: x.price, image: x.image, category: x.category, recurrence: x.recurrence || "" }); }} className="text-xs font-semibold text-[#1e3a5f] hover:underline min-h-[44px]">Modifica</button>
                  <button data-testid={`toggle-extra-${x.id}`} onClick={async () => { await api.patch(`/admin/extras/${x.id}`, { ...x, active: !x.active }); load(); }} className="text-xs font-semibold text-[#4a7c59] hover:underline min-h-[44px]">
                    {x.active ? "Disattiva" : "Attiva"}
                  </button>
                  <button data-testid={`delete-extra-${x.id}`} onClick={async () => { await api.delete(`/admin/extras/${x.id}`); load(); }} className="text-xs font-semibold text-red-700 hover:underline min-h-[44px]">Elimina</button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={saveExtra} className="card-ivory p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 items-end" data-testid="extra-form">
          <Field label="Nome"><input data-testid="extra-name" className="input-soft" required value={extraForm.name} onChange={num(extraForm, setExtraForm, "name")} /></Field>
          <Field label="Descrizione"><input data-testid="extra-desc" className="input-soft" value={extraForm.description} onChange={num(extraForm, setExtraForm, "description")} /></Field>
          <Field label="Prezzo €"><input data-testid="extra-price" type="number" step="0.5" min="0" className="input-soft" required value={extraForm.price} onChange={num(extraForm, setExtraForm, "price")} /></Field>
          <Field label="Categoria">
            <select data-testid="extra-category" className="input-soft" value={extraForm.category} onChange={num(extraForm, setExtraForm, "category")}>
              <option value="extra">Extra</option>
              <option value="ricorrenza">Ricorrenza</option>
            </select>
          </Field>
          {extraForm.category === "ricorrenza" && (
            <Field label="Ricorrenza">
              <select data-testid="extra-recurrence" className="input-soft" value={extraForm.recurrence} onChange={num(extraForm, setExtraForm, "recurrence")}>
                <option value="">—</option>
                {Object.entries(REC_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
          )}
          <Field label="URL fotografia"><input data-testid="extra-image" className="input-soft" value={extraForm.image} onChange={num(extraForm, setExtraForm, "image")} /></Field>
          <button data-testid="extra-save" type="submit" className="btn-primary !py-2.5 text-sm min-h-[44px] lg:col-span-3 sm:col-span-2">
            {editingExtra ? "Salva modifiche" : "Aggiungi prodotto"}
          </button>
        </form>
      </Section>

      <Section title="Piani abbonamento" count={plans.length} icon={Package} testid="admin-plans">
        <div className="card-soft overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-soft border-b border-[#e2e8f0]">
                <th className="p-4">Durata</th><th className="p-4">Frequenza</th><th className="p-4">Interventi</th><th className="p-4">Prezzo €</th><th className="p-4">Stato</th><th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id} className="border-b border-[#f1f5f9] last:border-0" data-testid={`admin-plan-${p.id}`}>
                  <td className="p-4 text-navy font-medium">{p.duration_months} {p.duration_months === 1 ? "mese" : "mesi"}</td>
                  <td className="p-4">{p.frequency}/mese{p.duration_months === 12 && p.frequency === 4 ? " (settimanale)" : ""}</td>
                  <td className="p-4">{p.interventions_total}</td>
                  <td className="p-4">
                    <input data-testid={`plan-price-${p.id}`} type="number" step="1" min="0" className="input-soft !w-28 !py-1.5" defaultValue={p.price}
                      onBlur={async (e) => { if (Number(e.target.value) !== p.price) { await api.patch(`/admin/plans/${p.id}`, { price: Number(e.target.value) }); toast.success("Prezzo aggiornato."); load(); } }} />
                  </td>
                  <td className="p-4">{p.active ? <span className="text-[#2f5d3e] font-semibold">Attivo</span> : <span className="text-muted-soft">Disattivo</span>}</td>
                  <td className="p-4">
                    <button data-testid={`plan-toggle-${p.id}`} onClick={async () => { await api.patch(`/admin/plans/${p.id}`, { active: !p.active }); load(); }} className="text-xs font-semibold text-[#1e3a5f] hover:underline min-h-[44px]">
                      {p.active ? "Disattiva" : "Attiva"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Offerta in evidenza" icon={Tag} testid="admin-offer">
        <form onSubmit={saveOffer} className="card-soft p-6 grid sm:grid-cols-3 gap-4 items-end">
          <Field label="Titolo"><input data-testid="offer-title" className="input-soft" value={offer.title} onChange={num(offer, setOffer, "title")} /></Field>
          <Field label="Durata (mesi)"><input data-testid="offer-months" type="number" min="1" className="input-soft" value={offer.duration_months} onChange={num(offer, setOffer, "duration_months")} /></Field>
          <Field label="Interventi al mese"><input data-testid="offer-freq" type="number" min="1" max="4" className="input-soft" value={offer.frequency} onChange={num(offer, setOffer, "frequency")} /></Field>
          <Field label="Interventi totali"><input data-testid="offer-total" type="number" min="1" className="input-soft" value={offer.interventions_total} onChange={num(offer, setOffer, "interventions_total")} /></Field>
          <Field label="Prezzo €"><input data-testid="offer-price" type="number" step="0.5" className="input-soft" value={offer.price} onChange={num(offer, setOffer, "price")} /></Field>
          <Field label="Prezzo a intervento €"><input data-testid="offer-ppi" type="number" step="0.5" className="input-soft" value={offer.price_per_intervention} onChange={num(offer, setOffer, "price_per_intervention")} /></Field>
          <Field label="Confronto singole uscite €"><input data-testid="offer-compare" type="number" step="0.5" className="input-soft" value={offer.compare_price} onChange={num(offer, setOffer, "compare_price")} /></Field>
          <Field label="Risparmio €"><input data-testid="offer-savings" type="number" step="0.5" className="input-soft" value={offer.savings} onChange={num(offer, setOffer, "savings")} /></Field>
          <Field label="Badge"><input data-testid="offer-badge" className="input-soft" value={offer.badge} onChange={num(offer, setOffer, "badge")} /></Field>
          <button data-testid="offer-save" type="submit" className="btn-primary !py-2.5 text-sm min-h-[44px] sm:col-span-3">Salva offerta</button>
        </form>
      </Section>

      <Section title="Prezzi, costi e margini" icon={Settings} testid="admin-settings">
        <p className="text-sm text-muted-soft mb-4">Questi valori economici interni non vengono mostrati al cliente.</p>
        <form onSubmit={saveSettings} className="card-soft p-6 grid sm:grid-cols-3 gap-4 items-end">
          <Field label="Prezzo prima uscita (prova) €"><input data-testid="set-trial" type="number" step="0.5" className="input-soft" value={settings.trial_price} onChange={num(settings, setSettings, "trial_price")} /></Field>
          <Field label="Prezzo singola uscita € (sempre uguale, anche dalla 2ª)"><input data-testid="set-single" type="number" step="0.5" className="input-soft" value={settings.single_visit_price} onChange={num(settings, setSettings, "single_visit_price")} /></Field>
          <Field label="Margine piattaforma su singola uscita € (interno, NON applicato al prezzo)"><input data-testid="set-margin" type="number" step="0.5" className="input-soft" value={settings.single_visit_margin} onChange={num(settings, setSettings, "single_visit_margin")} /></Field>
          <Field label="Prezzo fiori standard €"><input data-testid="set-flower-std" type="number" step="0.5" className="input-soft" value={settings.flower_standard_price} onChange={num(settings, setSettings, "flower_standard_price")} /></Field>
          <Field label="Compenso professionista €/intervento"><input data-testid="set-pro-fee" type="number" step="0.5" className="input-soft" value={settings.professional_fee} onChange={num(settings, setSettings, "professional_fee")} /></Field>
          <Field label="Margine piattaforma €/intervento"><input data-testid="set-platform-margin" type="number" step="0.5" className="input-soft" value={settings.platform_margin} onChange={num(settings, setSettings, "platform_margin")} /></Field>
          <Field label="Pulizia approfondita € (la cura ordinaria è sempre inclusa)"><input data-testid="set-cleaning" type="number" step="0.5" className="input-soft" value={settings.cleaning_ordinary_price} onChange={num(settings, setSettings, "cleaning_ordinary_price")} /></Field>
          <Field label="Supplemento uscita dedicata Extra € (solo ordini di soli Extra senza intervento già programmato; mai con un'uscita o sulle ricorrenze)"><input data-testid="set-exit-fee" type="number" step="0.5" className="input-soft" value={settings.extra_exit_fee ?? 10} onChange={num(settings, setSettings, "extra_exit_fee")} /></Field>
          <Field label="Numero WhatsApp Business (formato internazionale, senza + né spazi)"><input data-testid="set-whatsapp" type="text" className="input-soft" value={settings.whatsapp_number ?? ""} onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })} placeholder="393514545412" /></Field>
          <Field label="Tipi di luogo (separati da virgola)">
            <input data-testid="set-grave-types" className="input-soft" value={settings.grave_types.join(", ")}
              onChange={(e) => setSettings({ ...settings, grave_types: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
          </Field>
          <button data-testid="settings-save" type="submit" className="btn-primary !py-2.5 text-sm min-h-[44px]">Salva prezzi</button>
        </form>
      </Section>

      {plans.length === 0 && <EmptyState title="Nessun piano configurato" />}
    </div>
  );
}
